/**
 * Root Navigator
 *
 * Stack:
 *   Auth Stack   → Login, Register, OTP verification
 *   Main Tabs    → Home, Jobs, History, Training, Profile
 *
 * Active delivery banner shown at top of tabs when a job is in progress.
 */
import React, { useEffect } from 'react';
import { NavigationContainer, useNavigation, useNavigationContainerRef } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator, BottomTabBar } from '@react-navigation/bottom-tabs';
import { useSelector } from 'react-redux';
import { Ionicons } from '@expo/vector-icons';
import { Platform, View, Text, TouchableOpacity, StyleSheet, useWindowDimensions } from 'react-native';
import { RootState, loadPersistedState } from '../store';
import { colors, fonts, RD } from '../theme/colors';
import { reportPresence, loadedAppVersion } from '../services/presence';
import { startLocationTracking, stopLocationTracking } from '../services/location';
import { captureAndUpload } from '../services/screenCapture';
import { useSupportNotifications } from '../services/support-notifications';
import { consumePendingPredispatch } from '../services/predispatch-deeplink';

// Auth screens
import { LoginScreen }    from '../screens/auth/LoginScreen';
import { RegisterScreen } from '../screens/auth/RegisterScreen';

// Main tab screens
import { HomeScreen }     from '../screens/home/HomeScreen';
import { JobBoardScreen } from '../screens/job/JobBoardScreen';
import { JobDetailScreen }from '../screens/job/JobDetailScreen';
import { ActiveJobScreen }from '../screens/active/ActiveJobScreen';
import { ChecklistScreen }from '../screens/checklist/ChecklistScreen';
import { PreDispatchChecklistScreen } from '../screens/predispatch/PreDispatchChecklistScreen';
import { SignatureScreen } from '../screens/checklist/SignatureScreen';
import { LoadedPhotoScreen } from '../screens/checklist/LoadedPhotoScreen';
import { MessagingScreen } from '../screens/messaging/MessagingScreen';
import { SupportInboxScreen } from '../screens/messaging/SupportInboxScreen';
import { FleetMessagesScreen } from '../screens/messaging/FleetMessagesScreen';
import { EarningsScreen } from '../screens/earnings/EarningsScreen';
import { TrainingScreen } from '../screens/training/TrainingScreen';
import { CourseScreen }   from '../screens/training/CourseScreen';
import { QuizScreen }     from '../screens/training/QuizScreen';
import { ProfileScreen }  from '../screens/profile/ProfileScreen';
import { FleetScreen }    from '../screens/fleet/FleetScreen';
import { JobHistoryScreen } from '../screens/history/JobHistoryScreen';
import { EditProfileScreen } from '../screens/profile/EditProfileScreen';
import { StorageScreen } from '../screens/storage/StorageScreen';
import { StorageDropOffScreen } from '../screens/storage/StorageDropOffScreen';

const Stack = createNativeStackNavigator();
const Tab   = createBottomTabNavigator();

// Screens where a silent reload is safe (no unsaved in-progress work). The reload is HELD
// on any input/flow screen (Active job, Checklist, Signature, LoadedPhoto, Messaging,
// PreDispatch, Quiz, EditProfile, StorageDropOff) until the driver returns to one of these
// or backgrounds the app — so an auto-update can never wipe a half-done signature/photo/text.
const SAFE_TO_RELOAD_SCREENS = new Set(['Home', 'Jobs', 'Fleet', 'History', 'Training', 'Profile']);
const versionNum = (v?: string | null): number => { const m = /v(\d+)/.exec(v || ''); return m ? parseInt(m[1], 10) : NaN; };

/**
 * Self-update: when the backend reports a newer app version than the one this PWA loaded
 * with (localStorage __app_reset, set by the deploy boot script), clear the asset caches and
 * reload to fetch the new bundle — the only way a deployed fix reaches drivers who keep the
 * app open all day. Safeguards: (1) only moves FORWARD (never reload to an older/garbled
 * version); (2) only reloads on a passive screen or while backgrounded, never mid-task;
 * (3) sets __app_reset to the new version first so the boot script SKIPS its localStorage
 * wipe → no forced re-login (relies on backend CURRENT_DRIVER_APP_VERSION == the deployed
 * RESET_VERSION — keep them in sync on every mobile deploy); (4) sessionStorage one-shot so
 * it can never loop.
 */
/**
 * Clear all asset caches + unregister service workers, then hard-reload — the most
 * forceful way to pull a fresh bundle. Used by both the silent auto-update and the manual
 * "Update available" banner tap. Setting __app_reset to `latest` first makes the boot script
 * SKIP its localStorage wipe, so the driver keeps their session (no forced re-login).
 */
function applyUpdate(latest?: string) {
  if (typeof window === 'undefined') return;
  try {
    if (latest) { try { localStorage.setItem('__app_reset', latest); } catch { /* ignore */ } }
    const reload = () => { try { window.location.reload(); } catch { /* ignore */ } };
    const tasks: Promise<any>[] = [];
    if ('caches' in window) tasks.push((caches as any).keys().then((ns: string[]) => Promise.all(ns.map(k => (caches as any).delete(k)))).catch(() => {}));
    if (typeof navigator !== 'undefined' && (navigator as any).serviceWorker) {
      tasks.push((navigator as any).serviceWorker.getRegistrations().then((rs: any[]) => Promise.all(rs.map(r => r.unregister()))).catch(() => {}));
    }
    if (tasks.length) Promise.all(tasks).finally(reload); else reload();
  } catch { try { window.location.reload(); } catch { /* ignore */ } }
}

/**
 * Self-update evaluator. Compares the TRUE loaded build version (baked-in BUILD_VERSION via
 * loadedAppVersion — never the optimistic flag) against the backend's latest, and:
 *   • 'current'  — up to date (or ahead); nothing to do.
 *   • 'applied'  — behind AND on a safe screen AND not already retried → reloading now (silent).
 *   • 'pending'  — behind but held (mid-task screen, or a prior auto-reload didn't take) →
 *                  the caller surfaces the manual "Update available" banner so the driver can
 *                  update with one tap the moment it's convenient. This is the reliable path
 *                  for a driver who lives on the Active-job/Checklist screen all day, and the
 *                  escape hatch when a stale CDN reload leaves the app genuinely behind.
 * Safeguards: only ever moves FORWARD (never reload to an older/garbled version); a
 * sessionStorage one-shot stops the silent path from looping.
 */
function evaluateUpdate(latest: string | undefined, routeName: string | undefined): 'current' | 'applied' | 'pending' {
  if (!latest || Platform.OS !== 'web' || typeof window === 'undefined') return 'current';
  try {
    const current = loadedAppVersion();                    // ground truth (baked build version)
    const cn = versionNum(current), ln = versionNum(latest);
    if (isNaN(ln)) return 'current';                        // can't parse target — do nothing
    if (!isNaN(cn) && ln <= cn) return 'current';          // up to date or ahead
    // We are BEHIND the latest shipped build.
    const backgrounded = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const safeNow = backgrounded || (!!routeName && SAFE_TO_RELOAD_SCREENS.has(routeName));
    if (!safeNow) return 'pending';                        // mid-task — offer the banner instead
    if (sessionStorage.getItem('__upd_attempt') === latest) return 'pending'; // auto-reload didn't take → let them tap
    sessionStorage.setItem('__upd_attempt', latest);       // one-shot per version for the silent path
    applyUpdate(latest);
    return 'applied';
  } catch { return 'current'; }
}

const TAB_ICONS: Record<string, { focused: keyof typeof Ionicons.glyphMap; default: keyof typeof Ionicons.glyphMap }> = {
  Home:     { focused: 'home',           default: 'home-outline' },
  Jobs:     { focused: 'briefcase',      default: 'briefcase-outline' },
  Fleet:    { focused: 'people',         default: 'people-outline' },
  History:  { focused: 'time',           default: 'time-outline' },
  Training: { focused: 'school',         default: 'school-outline' },
  Profile:  { focused: 'person-circle',  default: 'person-circle-outline' },
};

const SIDEBAR_W = 248;
const DESKTOP_MIN = 1024; // px — at/above this, show the desktop sidebar instead of bottom tabs

/**
 * Desktop sidebar — rendered in place of the bottom tab bar on wide screens.
 * Receives the standard bottom-tab `tabBar` props (state/navigation/descriptors),
 * so it stays perfectly in sync with the real navigator and every screen works.
 */
function DesktopSidebar({ state, navigation, descriptors }: any) {
  return (
    <View style={sidebarStyles.sidebar}>
      <View style={sidebarStyles.brand}>
        <View style={sidebarStyles.brandMark}><Text style={sidebarStyles.brandMarkText}>C</Text></View>
        <Text style={sidebarStyles.brandText}>Commonplace</Text>
      </View>
      <View style={{ marginTop: 6 }}>
        {state.routes.map((route: any, i: number) => {
          const focused = state.index === i;
          const icons = TAB_ICONS[route.name] || TAB_ICONS.Home;
          const label = descriptors[route.key]?.options?.title || route.name;
          return (
            <TouchableOpacity
              key={route.key}
              accessibilityRole="button"
              onPress={() => {
                const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });
                if (!focused && !event.defaultPrevented) navigation.navigate(route.name);
              }}
              style={[sidebarStyles.item, focused && sidebarStyles.itemActive]}
            >
              <Ionicons name={focused ? icons.focused : icons.default} size={20} color={focused ? '#fff' : RD.textSec} />
              <Text style={[sidebarStyles.itemLabel, focused && sidebarStyles.itemLabelActive]}>{label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const sidebarStyles = StyleSheet.create({
  sidebar:        { position: 'absolute', left: 0, top: 0, bottom: 0, width: SIDEBAR_W, backgroundColor: RD.surface, borderRightWidth: 1, borderRightColor: RD.border, paddingTop: 20, paddingHorizontal: 14 },
  brand:          { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 8, paddingBottom: 18 },
  brandMark:      { width: 34, height: 34, borderRadius: 9, backgroundColor: RD.maroon, alignItems: 'center', justifyContent: 'center' },
  brandMarkText:  { color: '#fff', fontWeight: '800', fontSize: 18 },
  brandText:      { fontSize: 17, fontWeight: '700', color: RD.text },
  item:           { flexDirection: 'row', alignItems: 'center', gap: 12, height: 44, paddingHorizontal: 12, borderRadius: 10, marginBottom: 4 },
  itemActive:     { backgroundColor: RD.ink },
  itemLabel:      { fontSize: 14, fontWeight: '600', color: RD.textSec },
  itemLabelActive:{ color: '#fff' },
});

function MainTabs() {
  const driver = useSelector((s: RootState) => s.auth.driver);
  const isFleetManager = !!(driver as any)?.isFleetManager;
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isWide = width >= DESKTOP_MIN;
  const { unreadCount } = useSupportNotifications();

  return (
    <View style={{ flex: 1 }}>
      <Tab.Navigator
        tabBar={(props) => (isWide ? <DesktopSidebar {...props} /> : <BottomTabBar {...props} />)}
        sceneContainerStyle={{ paddingLeft: isWide ? SIDEBAR_W : 0 }}
        screenOptions={({ route }) => ({
          tabBarActiveTintColor:   colors.black,
          tabBarInactiveTintColor: colors.gray40,
          tabBarStyle: {
            backgroundColor: colors.white,
            borderTopColor: colors.gray10,
            borderTopWidth: 1,
            height: Platform.OS === 'ios' ? 88 : 64,
            paddingTop: 8,
            paddingBottom: Platform.OS === 'ios' ? 28 : 8,
          },
          tabBarLabelStyle: {
            fontSize: 11,
            fontWeight: '600',
            marginTop: 2,
            fontFamily: fonts.medium,
          },
          headerShown: false,
          tabBarIcon: ({ focused, color, size }) => {
            const icons = TAB_ICONS[route.name];
            const iconName = focused ? icons.focused : icons.default;
            return <Ionicons name={iconName} size={22} color={color} />;
          },
        })}
      >
        <Tab.Screen name="Home"     component={HomeScreen}       options={{ title: 'Home'     }} />
        <Tab.Screen name="Jobs"     component={JobBoardScreen}   options={{ title: 'Jobs'     }} />
        {isFleetManager && (
          <Tab.Screen name="Fleet"  component={FleetScreen}      options={{ title: 'Fleet'    }} />
        )}
        <Tab.Screen name="History"  component={JobHistoryScreen} options={{ title: 'History'  }} />
        <Tab.Screen name="Training" component={TrainingScreen}   options={{ title: 'Training' }} />
        <Tab.Screen name="Profile"  component={ProfileScreen}    options={{ title: 'Profile'  }} />
      </Tab.Navigator>

      {/* Floating support chat button — bottom right */}
      <TouchableOpacity
        style={floatingStyles.fab}
        onPress={() => navigation.navigate('SupportInbox')}
        activeOpacity={0.8}
      >
        <Ionicons name="chatbubble-ellipses" size={22} color={colors.white} />
        {unreadCount > 0 && (
          <View style={floatingStyles.fabBadge}>
            <Text style={floatingStyles.fabBadgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
          </View>
        )}
      </TouchableOpacity>
      {/* Fleet manager: fleet messaging lives in the Fleet tab (no separate inbox FAB). */}
    </View>
  );
}

const floatingStyles = StyleSheet.create({
  fab: {
    position: 'absolute',
    right: 16,
    bottom: Platform.OS === 'ios' ? 100 : 76,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.black,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  fabBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 5,
    backgroundColor: RD.maroon,
    borderWidth: 2,
    borderColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fabBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
  },
  fabFleet: {
    position: 'absolute',
    right: 16,
    bottom: Platform.OS === 'ios' ? 160 : 136,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#620E3B', // brand maroon — distinguishes from support FAB
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
});

export function RootNavigator() {
  const token = useSelector((s: RootState) => s.auth.token);
  const activeJobs = useSelector((s: RootState) => s.jobs.activeJobs);

  // Reload the driver's profile + certifications whenever they sign in. Certs were
  // previously only fetched once on cold boot, so a fresh login left them empty and
  // every certified job wrongly showed "Training Required" (e.g. Norman + Peloton).
  useEffect(() => {
    if (token) { loadPersistedState().catch(() => {}); }
  }, [token]);

  // Live presence: report the current screen (+ relevant job) to the backend on
  // every navigation change and a periodic heartbeat, so admin can see live activity.
  const navRef = useNavigationContainerRef();
  const activeJobRef = React.useRef<any>(null);
  activeJobRef.current = activeJobs?.[0] || null;

  // Screen capture is GATED on an admin actually watching this driver's Live tab — the
  // presence response tells us. html2canvas is a blocking main-thread rasterization and the
  // upload is a ~MB POST, so running it continuously for every logged-in driver all day was a
  // battery/bandwidth + privacy cost for nothing. Now it only fires while a tab is open.
  // Presence beats every 12s, but the html2canvas capture is EXPENSIVE (blocking raster) — cap it
  // to at most once per 30s so a watched driver's phone isn't hammered every heartbeat. Presence
  // (screen name) still updates every 12s; only the screenshot is throttled. This restores the
  // known-good 30s cadence that a later change accidentally fused back to the 12s heartbeat.
  const lastCaptureRef = React.useRef(0);
  const captureIfWatched = React.useCallback((watched: boolean) => {
    if (!watched) return;
    const now = Date.now();
    if (now - lastCaptureRef.current < 30000) return;
    lastCaptureRef.current = now;
    const ric = (globalThis as any).requestIdleCallback;
    if (typeof ric === 'function') ric(() => { captureAndUpload(); }, { timeout: 3000 });
    else setTimeout(() => { captureAndUpload(); }, 0);
  }, []);

  // "Update available" banner state: the newest shipped version and whether this device is
  // behind it but couldn't silently auto-reload (held mid-task, or a prior reload didn't take).
  const [pendingVersion, setPendingVersion] = React.useState<string | null>(null);

  const reportCurrent = React.useCallback(() => {
    try {
      const route = navRef.getCurrentRoute?.();
      if (!route) return;
      const jobId = (route.params as any)?.jobId || activeJobRef.current?.id || null;
      reportPresence(route.name, jobId).then(r => {
        captureIfWatched(r.watched);
        const status = evaluateUpdate(r.latestVersion, route.name);   // may silently reload if safe
        setPendingVersion(status === 'pending' ? (r.latestVersion || null) : null);
      }).catch(() => {});
    } catch { /* ignore */ }
  }, [navRef, captureIfWatched]);

  // Presence heartbeat (screen name + job) — also the capture trigger. ~12s so an admin
  // opening the Live tab sees the driver's screen within a few seconds, while capture stays
  // off entirely when no one is watching.
  React.useEffect(() => {
    if (!token) return;
    reportCurrent();
    const id = setInterval(reportCurrent, 12000);
    return () => clearInterval(id);
  }, [token, reportCurrent]);

  // App-wide CONTINUOUS location tracking — runs on EVERY screen for as long as the
  // driver is logged in and the app is open (web: watchPosition stream + backstop;
  // native: foreground poll). This is the fix for "no location in days/weeks": the old
  // loop lived only in HomeScreen and stopped the moment a driver left that tab. Reads
  // the live active-job count via a ref so cadence adapts without restarting the watch.
  const activeCountRef = React.useRef(0);
  activeCountRef.current = activeJobs?.length || 0;
  React.useEffect(() => {
    if (!token) { stopLocationTracking(); return; }
    startLocationTracking(() => activeCountRef.current > 0);
    return () => stopLocationTracking();
  }, [token]);

  // Pre-dispatch deep link (?predispatch=<token>). Once the driver is authenticated
  // (token present) and the navigator is mounted, resolve any pending token and
  // navigate into the PreDispatchChecklist for that exact job. Runs once per login;
  // if the link opened before sign-in, this is where the deferred handling fires.
  // No-op (and a no-op import side-effect) when PREDISPATCH_ENABLED is false.
  const predispatchConsumedRef = React.useRef(false);
  React.useEffect(() => {
    if (!token) { predispatchConsumedRef.current = false; return; }
    if (predispatchConsumedRef.current) return;
    predispatchConsumedRef.current = true;
    // Defer one tick so the authenticated stack (incl. PreDispatchChecklist) has
    // registered before we attempt to navigate.
    const id = setTimeout(() => {
      consumePendingPredispatch((jobId) => {
        try {
          if (navRef.isReady?.()) (navRef as any).navigate('PreDispatchChecklist', { jobId });
        } catch { /* ignore */ }
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(id);
  }, [token, navRef]);

  return (
    <>
    {token && pendingVersion ? (
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => { setPendingVersion(null); applyUpdate(pendingVersion || undefined); }}
        style={updateBar.wrap}
      >
        <Ionicons name="arrow-down-circle" size={18} color="#fff" />
        <Text style={updateBar.text}>New version available — tap to update</Text>
      </TouchableOpacity>
    ) : null}
    <NavigationContainer
      ref={navRef}
      onReady={() => { if (token) reportCurrent(); }}
      onStateChange={() => { if (token) reportCurrent(); }}
    >
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {!token ? (
          // Auth flow
          <>
            <Stack.Screen name="Login"    component={LoginScreen}    />
            <Stack.Screen name="Register" component={RegisterScreen} />
          </>
        ) : (
          // Authenticated flow
          <>
            <Stack.Screen name="Main"      component={MainTabs}       />
            <Stack.Screen name="JobDetail" component={JobDetailScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="ActiveJob" component={ActiveJobScreen} options={{ presentation: 'fullScreenModal' }} />
            <Stack.Screen name="Checklist" component={ChecklistScreen} options={{ presentation: 'card' }} />
            {/* Pre-dispatch gate. Registered always so deep links resolve, but only
                reached when PREDISPATCH_ENABLED is true (HomeScreen popup + en-route
                rejection route into it). fullScreenModal so it reads as a blocking flow. */}
            <Stack.Screen name="PreDispatchChecklist" component={PreDispatchChecklistScreen} options={{ presentation: 'fullScreenModal' }} />
            <Stack.Screen name="Signature" component={SignatureScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="LoadedPhoto" component={LoadedPhotoScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="Messaging" component={MessagingScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="SupportInbox" component={SupportInboxScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="FleetMessages" component={FleetMessagesScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="Course"    component={CourseScreen}    options={{ presentation: 'card' }} />
            <Stack.Screen name="Quiz"      component={QuizScreen}      options={{ presentation: 'card' }} />
            <Stack.Screen name="EditProfile" component={EditProfileScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="Storage" component={StorageScreen} options={{ presentation: 'card' }} />
            <Stack.Screen name="StorageDropOff" component={StorageDropOffScreen} options={{ presentation: 'card' }} />
          </>
        )}
      </Stack.Navigator>
    </NavigationContainer>
    </>
  );
}

// Tappable "update available" bar pinned to the very top. Web-only in practice (evaluateUpdate
// only returns 'pending' on web). Sits above the nav so a driver parked on the Active-job or
// Checklist screen — where the silent auto-reload is intentionally held so it can't wipe an
// in-progress signature/photo — can still pull the newest build with one tap when ready.
const updateBar = StyleSheet.create({
  wrap: {
    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 9999,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: '#620E3B', paddingTop: Platform.OS === 'web' ? 8 : 44, paddingBottom: 8, paddingHorizontal: 12,
  },
  text: { color: '#fff', fontSize: 13, fontWeight: '700' },
});
